import { Trans } from '@lingui/react';
import { Button } from '@we-make-websites/ui-lib';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { useCurrentRefinements } from 'react-instantsearch';
import { useIntersectionObserverRef } from 'rooks';
import aa from 'search-insights';
import { useContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import type {
  StoreFilterSetting,
  FiltersType,
} from 'components/globalContentContext/GlobalContentContext';
import { GlobalContentContext } from 'components/globalContentContext/GlobalContentContext';
import { OverlayContext } from 'components/overlay/OverlayContext';
import type { AlgoliaIndexLabel } from 'utils/algoliaConstants';
import { algoliaProductIndex } from 'utils/algoliaConstants';
import { VirtualRefinementList } from '../algoliaHelpers/VirtualRefinementList';
import { ActiveFilters } from './ActiveFilters';
import styles from './CollectionFilters.module.scss';
import { Filters } from './Filters';
import { MobileFiltersOverlay } from './MobileFiltersOverlay';
import { Sorting } from './Sorting';

export const CollectionFilters = ({
  filtersType = 'product',
  showSorting = true,
  indexType = 'product',
  isSearchPage = false,
  isEduCourse = false,
}: {
  filtersType?: FiltersType;
  showSorting?: boolean;
  indexType?: AlgoliaIndexLabel;
  isSearchPage?: boolean;
  isEduCourse?: boolean;
}) => {
  const { filterSettings } = useContext(GlobalContentContext);
  const { toggle: toggleOverlay } = useContext(OverlayContext);
  const [isFiltersStuck, setFiltersStuck] = useState(false);
  const { items } = useCurrentRefinements();
  const { locale: routerLocale } = useRouter();
  const locale = storeLocale(routerLocale);

  const intersectionCallback = (entries: IntersectionObserverEntry[]) => {
    if (entries && entries[0]) {
      const isOnScreen = entries[0].isIntersecting;
      setFiltersStuck(!isOnScreen);
    }
  };

  const [stickyTrackerRef] = useIntersectionObserverRef(intersectionCallback);

  const filtersArray = filterSettings?.[filtersType];
  const filters = filtersArray
    ? filtersArray.filter((filterSetting: StoreFilterSetting) =>
        Boolean(filterSetting)
      )
    : [];

  useEffect(() => {
    const initialFilters = items.map(
      (filter) =>
        `${
          filters.find(
            (item) => item.attribute === filter.refinements[0].attribute
          )?.searchParameter
        }:${filter.refinements[0].value}`
    );
    if (initialFilters.length > 0) {
      aa('viewedFilters', {
        index: algoliaProductIndex(locale),
        eventName: 'Filters Viewed',
        filters: [...initialFilters],
      });
    }
  }, []);

  const mobileFiltersOverlayNamespace = 'mobileFilterDrawer';

  const isNailArt = indexType === 'nailart';
  const pageTypeNailArt = !isSearchPage && isNailArt;

  /**
   * Whether to show the hits count in the active filters bar.
   * - Only enabled for nail art results as per the designs.
   */
  const showCount = isNailArt;

  return (
    <>
      <div
        className={styles.collectionFilters__stickyTracker}
        ref={stickyTrackerRef}
      />

      <section
        className={clsx(
          styles.collectionFilters,
          pageTypeNailArt && styles['collectionFilters--nailArt'],
          pageTypeNailArt &&
            items.length === 0 &&
            styles['collectionFilters--nailArtLightBackground']
        )}
        data-stuck={isFiltersStuck}
      >
        <div className={styles.collectionFilters__main}>
          <div className="container">
            <div className={styles.collectionFilters__mobile}>
              {!isSearchPage && isNailArt && (
                <Button
                  className={clsx(
                    styles.collectionFilters__mobileButton,
                    styles['collectionFilters__mobileButton--nailArt']
                  )}
                  type="button"
                  modifiers={['outlined', 'white']}
                  onClick={() => {
                    toggleOverlay({ namespace: mobileFiltersOverlayNamespace });
                  }}
                >
                  <span>
                    <Trans
                      id="filters.filterByNailArt"
                      message="Filter Nail Art Tutorials"
                    />
                  </span>
                </Button>
              )}

              {(isSearchPage || !isNailArt) && (
                <Button
                  className={styles.collectionFilters__mobileButton}
                  type="button"
                  modifiers={['outlined', 'mini']}
                  onClick={() => {
                    toggleOverlay({ namespace: mobileFiltersOverlayNamespace });
                  }}
                >
                  <span>
                    <Trans id="filters.filterBy" message="Filter Options" />
                  </span>
                </Button>
              )}
              {showSorting && <Sorting mobile isEduCourse={isEduCourse} />}
            </div>

            <div className={styles.collectionFilters__desktop}>
              <Filters filters={filters} />
              {showSorting && <Sorting isEduCourse={isEduCourse} />}
            </div>
          </div>
        </div>
        {filters.map((filter) => (
          <VirtualRefinementList
            key={filter.attribute}
            attribute={filter.attribute}
          />
        ))}

        <MobileFiltersOverlay
          namespace={mobileFiltersOverlayNamespace}
          filters={filters}
        />
      </section>

      <div className="container">
        <div
          className={clsx(
            styles.collectionFilters__activeFiltersContainer,
            !isSearchPage &&
              isNailArt &&
              styles['collectionFilters__activeFiltersContainer--nailArt']
          )}
        >
          <ActiveFilters showCount={showCount} />
        </div>
      </div>
    </>
  );
};
